import type { SiteLocale } from '@/i18n/locale'
import { projectDemoCopy, type ProjectDemoCopy } from '@/i18n/projects'

export type ProjectDemoId = 'lead-enrichment' | 'dental-landing' | 'law-firm' | 'translation-platform'

export type ProjectRelatedLink = {
  to: string
  hash?: string
  localize?: boolean
  label: Record<SiteLocale, string>
}

export type ProjectDemo = {
  id: ProjectDemoId
  path: string
  video: string
  poster: string
  width: number
  height: number
  duration: string
  uploadDate: string
  copy: Record<SiteLocale, ProjectDemoCopy>
  related: ProjectRelatedLink[]
}

export const LEAD_ENRICHMENT_PATH = '/projects/enrichissement-leads-ia'
export const DENTAL_LANDING_PATH = '/projects/landing-page-dentaire-automatisation-whatsapp'
export const LAW_FIRM_SITE_PATH = '/projects/site-cabinet-avocats'
export const TRANSLATION_PLATFORM_PATH = '/projects/plateforme-traduction-commandes'

export const PROJECT_DEMOS: ProjectDemo[] = [
  {
    id: 'translation-platform',
    path: TRANSLATION_PLATFORM_PATH,
    video: '/media/projects/plateforme-traduction-commandes.mp4',
    poster: '/media/projects/plateforme-traduction-commandes-poster.webp',
    width: 2542,
    height: 1080,
    duration: 'PT57S',
    uploadDate: '2026-08-26',
    copy: projectDemoCopy['translation-platform'],
    related: [
      { to: '/services', label: { en: 'Our services', fr: 'Nos services', ar: 'خدماتنا' } },
      { to: '/projects/demo-agent-ia-whatsapp-darija', label: { en: 'Darija WhatsApp agent demonstration', fr: 'Démonstration d’un agent WhatsApp en darija', ar: 'عرض وكيل واتساب بالدارجة' } },
      { to: '/contact', label: { en: 'Contact', fr: 'Contact', ar: 'اتصل بنا' } },
    ],
  },
  {
    id: 'dental-landing',
    path: DENTAL_LANDING_PATH,
    video: '/media/projects/landing-page-dentaire-whatsapp-demo.mp4',
    poster: '/media/projects/landing-page-dentaire-whatsapp-demo-poster.webp',
    width: 1920,
    height: 840,
    duration: 'PT1M10S',
    uploadDate: '2026-08-26',
    copy: projectDemoCopy['dental-landing'],
    related: [
      { to: '/ma/specialites/dentistes', label: { en: 'AI solutions for dentists in Morocco', fr: 'Solutions IA pour dentistes au Maroc', ar: 'حلول الذكاء الاصطناعي لأطباء الأسنان في المغرب' } },
      { to: '/dentaire', label: { en: 'WhatsApp AI agent for dental clinics', fr: 'Agent IA WhatsApp pour cabinets dentaires', ar: 'وكيل واتساب بالذكاء الاصطناعي لعيادات الأسنان' } },
      { to: '/projects/demo-agent-ia-whatsapp-darija', label: { en: 'Darija WhatsApp agent demonstration', fr: 'Démonstration d’un agent WhatsApp en darija', ar: 'عرض وكيل واتساب بالدارجة' } },
    ],
  },
  {
    id: 'law-firm',
    path: LAW_FIRM_SITE_PATH,
    video: '/media/projects/site-cabinet-avocats-demo.mp4',
    poster: '/media/projects/site-cabinet-avocats-demo-poster.webp',
    width: 1328,
    height: 598,
    duration: 'PT21S',
    uploadDate: '2026-03-02',
    copy: projectDemoCopy['law-firm'],
    related: [
      { to: '/services', label: { en: 'Our services', fr: 'Nos services', ar: 'خدماتنا' } },
      { to: '/contact', label: { en: 'Contact', fr: 'Contact', ar: 'اتصل بنا' } },
    ],
  },
  {
    id: 'lead-enrichment',
    path: LEAD_ENRICHMENT_PATH,
    video: '/media/projects/pipeline-enrichissement-leads-ia.mp4',
    poster: '/media/projects/pipeline-enrichissement-leads-ia-poster.webp',
    width: 1916,
    height: 868,
    duration: 'PT4M32S',
    uploadDate: '2026-05-29',
    copy: projectDemoCopy['lead-enrichment'],
    related: [
      { to: '/services', hash: '#workflow-automations', label: { en: 'Workflow automations', fr: 'Automatisation des workflows', ar: 'أتمتة سير العمل' } },
      { to: '/automations', label: { en: 'Automation library', fr: 'Bibliothèque d’automatisations', ar: 'مكتبة الأتمتة' } },
      { to: '/resources/how-ai-workflow-automation-works', localize: false, label: { en: 'How AI workflow automation works', fr: 'How AI workflow automation works', ar: 'How AI workflow automation works' } },
    ],
  },
]

export const PROJECT_DEMO_PATHS = PROJECT_DEMOS.map((demo) => demo.path.slice(1))

export function getProjectDemo(pathname: string) {
  const logical = pathname.replace(/^\/(en|fr|ar)(?=\/)/, '') || pathname
  return PROJECT_DEMOS.find((demo) => logical === demo.path || logical.endsWith(demo.path))
}
